import {
  NavBar as CDSNavBar,
  IContextSwitcherOptions,
  INavBarOptions,
  IUtilityTrayOptions,
  IOGAgentOptions,
} from "@opengov/components-nav-bar";
import { useClaimAccess, useUnreadCount } from "@opengov/bnp-core";
import * as React from "react";
import { IntlProvider, useIntl } from "react-intl";

import { OgAssistSearchDialog, OgAssistSearchDialogProps } from "../OgAssistSearchDialog";
import messages from "./locales/en-US.json";
import { useGetEntityAppEntitlements } from "./hooks/useGetEntityAppEntitlements";
import { usePspOptions } from "../PspOptionsContext/usePspOptions";
import { APPS_REQUIRE_ENTITLEMENTS, getActiveProductKey } from "./utils/activeProductUtils";
import { getAppName } from "./utils/appNameUtils";
import { DEFAULT_LOCALE } from "./utils/intl";
import { getMenuOptions } from "./utils/menuItemsUtils";
import { User } from "./utils/profileSettingsOptionsUtils";
import { getNewActionOptions } from "./utils/newActionOptionsUtils";
import { getUtilityTrayOptions } from "./utils/utilityTrayOptionsUtils";
import { CoAMigrationBanner } from "./banners/CoAMigrationBanner";
import { NewRequestDialog } from "./modals/NewRequestDialog/NewRequestDialog";
import { ControlPanelMenuItem, GlobalNavApplicationId } from "./types";
import { getFilteredMenuItems } from "./utils/controlPanelMenuItemsUtils";
import { Routes } from "./utils/routesUtils";
import { NotificationDrawer } from "../NotificationsDrawer/NotificationDrawer";

export type RequiredIContextSwitcherOptions = Omit<IContextSwitcherOptions, "activeProductKey">;
export type NavBarProps = {
  activeAppKey: GlobalNavApplicationId;
  /** @deprecated Menu items are now computed internally. Will be removed in a future version. */
  controlPanelMenuItems?: ControlPanelMenuItem[];
  entitySubdomain: string;
  user: User;
  entityUuid?: string;
  /** @deprecated No longer used for workforce authorization. Will be removed in a future version. */
  entityFeaturePermissions?: string[];
  contextSwitcherOptions?: RequiredIContextSwitcherOptions;
  hideClassicReporting?: boolean;
  isFormAndWorkflowsFeatureEnabled?: boolean;
  isPerformanceManagementFeatureEnabled?: boolean;
  isPerformanceLimitedFeaturesEnabled?: boolean;
  isWorkforcePlanning2Enabled?: boolean;
  ogAssistSearchProps?: Omit<OgAssistSearchDialogProps, "open" | "onClose" | "isWorkforceAuthorized">;
  handleSignOut: () => void;
  banner?: React.ReactNode;
  loggerOutput?: INavBarOptions["loggerOutput"];
  agentsBaseUrl?: string;
  applicationContext?: IOGAgentOptions["applicationContext"];
  ogAssistDrawerOptions?: Pick<IOGAgentOptions, "onDrawerToggle" | "requestDrawerOpen"> & {
    drawerPortalTargetRef?: React.RefObject<HTMLElement>;
  };
  notificationsUrl?: string;
  onViewAllNotificationsClick?: () => void;
};

export const NavBarView = ({
  activeAppKey,
  controlPanelMenuItems,
  user,
  entitySubdomain,
  entityUuid,
  entityFeaturePermissions,
  contextSwitcherOptions,
  hideClassicReporting,
  isFormAndWorkflowsFeatureEnabled,
  isPerformanceManagementFeatureEnabled,
  isPerformanceLimitedFeaturesEnabled,
  isWorkforcePlanning2Enabled,
  notificationsUrl,
  ogAssistSearchProps,
  handleSignOut,
  banner,
  loggerOutput,
  agentsBaseUrl,
  applicationContext,
  ogAssistDrawerOptions,
  onViewAllNotificationsClick,
}: NavBarProps) => {
  const intl = useIntl();
  const pspOptions = usePspOptions();
  const [showOgAssistSearch, setShowOgAssistSearch] = React.useState<boolean>(false);
  const [showNewRequestDialog, setShowNewRequestDialog] = React.useState<boolean>(false);
  const appRequireEntitlements = APPS_REQUIRE_ENTITLEMENTS.includes(activeAppKey);
  const userUuid = user.uuid;
  const unreadCount = useUnreadCount(notificationsUrl, entityUuid);
  const notificationsConfig = notificationsUrl && entityUuid ? { count: unreadCount } : undefined;
  const [showNotificationDrawer, setShowNotificationDrawer] = React.useState<boolean>(false);
  const resolvedControlPanelMenuItems = React.useMemo(
    () =>
      controlPanelMenuItems ??
      getFilteredMenuItems({
        entitySubdomain,
        currentHost: window.location.host,
      }),
    [controlPanelMenuItems, entitySubdomain],
  );

  const { isFormSubmitter: isUserFormSubmitter, isBnpAdmin } = useClaimAccess({
    entityUuid,
    userUuid,
  });
  const isWorkforceAuthorized = isBnpAdmin;

  const entitlements = useGetEntityAppEntitlements({
    appRequireEntitlements,
    entityUuid,
    userUuid,
  });

  const activeProductKey = React.useMemo(
    () =>
      getActiveProductKey({
        activeAppKey,
        entitlements,
        entitlementsRequired: appRequireEntitlements,
      }),
    [activeAppKey, entitlements, appRequireEntitlements],
  );

  const resolvedLoggerOutput: INavBarOptions["loggerOutput"] =
    loggerOutput ?? (process.env.NODE_ENV === "development" ? "console" : "remote");

  const onCreateRequest = React.useCallback(() => {
    setShowNewRequestDialog(true);
  }, []);

  const onCreateKpiSubmission = React.useCallback(() => {
    window.location.href = Routes.newPerformanceKpiSubmission({
      entitySubdomain,
      baseRouterPath: pspOptions?.baseRouterPath,
    });
  }, [entitySubdomain, pspOptions?.baseRouterPath]);

  const createNewActionOptions = React.useMemo<IUtilityTrayOptions["createNewActionOptions"]>(() => {
    return getNewActionOptions({
      intl,
      isUserFormSubmitter,
      isFormAndWorkflowsFeatureEnabled,
      isPerformanceManagementFeatureEnabled,
      isPerformanceLimitedFeaturesEnabled,
      activeAppKey,
      onCreateRequest,
      onCreateKpiSubmission,
    });
  }, [
    intl,
    isUserFormSubmitter,
    isFormAndWorkflowsFeatureEnabled,
    isPerformanceManagementFeatureEnabled,
    isPerformanceLimitedFeaturesEnabled,
    activeAppKey,
    onCreateRequest,
    onCreateKpiSubmission,
  ]);

  const agentProps =
    contextSwitcherOptions?.auth0Config && agentsBaseUrl && entityUuid
      ? {
          entityUUID: entityUuid,
          agentsBaseUrl: agentsBaseUrl,
          auth0Config: contextSwitcherOptions.auth0Config,
          applicationContext: applicationContext,
          drawerPortalTargetRef: ogAssistDrawerOptions?.drawerPortalTargetRef,
          onDrawerToggle: ogAssistDrawerOptions?.onDrawerToggle,
          requestDrawerOpen: ogAssistDrawerOptions?.requestDrawerOpen,
        }
      : undefined;

  return (
    <>
      <CDSNavBar
        appName={getAppName({ intl, activeProductKey, activeAppKey })}
        menuOptions={getMenuOptions({
          activeProductKey,
          activeAppKey,
          controlPanelMenuItems: resolvedControlPanelMenuItems,
          entitySubdomain,
          entityFeaturePermissions,
          intl,
          isWorkforceAuthorized,
          hideClassicReporting,
          isFormAndWorkflowsFeatureEnabled,
          isPerformanceManagementFeatureEnabled,
          isWorkforcePlanning2Enabled,
        })}
        loggerOutput={resolvedLoggerOutput}
        contextSwitcherOptions={
          contextSwitcherOptions && {
            ...contextSwitcherOptions,
            activeProductKey,
          }
        }
        utilityTrayOptions={getUtilityTrayOptions({
          activeAppKey,
          activeProductKey,
          entitySubdomain,
          user,
          handleSignOut,
          intl,
          ogAssistSearchProps,
          handleSearchIconClick: () => {
            setShowOgAssistSearch(true);
          },
          handleNotificationIconClick: () => {
            setShowNotificationDrawer((prev) => !prev);
          },
          createNewActionOptions,
          notificationsConfig,
        })}
        {...(agentProps ? { unstable_ogAgentOptions: agentProps } : {})}
      />
      {banner}
      {ogAssistSearchProps ? (
        <OgAssistSearchDialog
          {...ogAssistSearchProps}
          isWorkforceAuthorized={isWorkforceAuthorized}
          open={showOgAssistSearch}
          onClose={() => {
            setShowOgAssistSearch(false);
          }}
        />
      ) : null}
      {isFormAndWorkflowsFeatureEnabled && showNewRequestDialog && entityUuid ? (
        <NewRequestDialog
          entitySubdomain={entitySubdomain}
          entityUuid={entityUuid}
          onClose={() => {
            setShowNewRequestDialog(false);
          }}
        />
      ) : null}
      {entityUuid && notificationsUrl && (
        <NotificationDrawer
          open={showNotificationDrawer}
          onClose={() => {
            setShowNotificationDrawer(false);
          }}
          entityId={entityUuid}
          baseUrl={notificationsUrl}
          onViewAllNotificationsClick={onViewAllNotificationsClick}
        />
      )}
    </>
  );
};

export const NavBar = (props: NavBarProps) => {
  return (
    <IntlProvider locale={DEFAULT_LOCALE} messages={messages}>
      <NavBarView {...props} />
    </IntlProvider>
  );
};
NavBar.CoAMigrationBanner = CoAMigrationBanner;
